import { Component, OnInit, OnDestroy, inject, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { Observable, combineLatest, Subject, takeUntil, take, map, switchMap, of, merge, timer, distinctUntilChanged, shareReplay, debounceTime, catchError } from "rxjs";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { MipsSubmissionCheckDialogComponent } from "@components/mips-submission-check-dialog/mips-submission-check-dialog.component";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { ReviewBoardActions } from "@store/actions/review-board/review-board.actions";
import {
  selectReviewBoardStatus,
  selectReviewBoardStatusLoading,
} from "@store/selectors/review-board/review-board.selectors";
import { selectHasAdminRights, selectIsSuperUser } from "@store/selectors/user/user.selectors";
import { ReviewBoardStatus } from "@models/review-board.model";
import {
  selectActivePacket,
  selectTinDetails,
  selectIsSubmitting,
  selectError,
  selectQuality,
  selectPromotingInteroperability,
  selectImprovementActivities,
  selectMultiPacketMode,
  selectSettings,
  selectDrcfStatus,
  selectDrcfEnvelopesLoading,
  selectTraditionalPacket,
  selectMvpPacket,
  selectIsSaving,
  selectEffectiveQuestionnaire,
} from "@store/selectors/mips-packet/mips-packet.selector";
import {
  MipsPacket,
  MipsQuestionnaire,
  TinEligibilityDetails,
} from "@models/mips-stepper.model";
import {
  MipsValidationService,
  StepValidationResult,
} from "@services/mips-validation.service";
import { DashboardService } from "@services/dashboard.service";
import {
  CalculateMipsReweightingRequest,
  CalculateMipsReweightingResponse,
  MipsCategoryWeights,
} from "@models/mips-reweighting-score.model";
import { DashboardMipsScoreBlock } from "@models/dashboard-mips-scores.model";
import { buildQualityAttestationInput } from "@services/mips-reweighting.service";
import { selectIsSubmitting as selectQuestionnaireIsSubmitting } from "@store/selectors/mips-questionnaire/mips-questionnaire.selectors";
import { selectMeasures } from "@store/selectors/dashboard/dashboard.selectors";
import { MipsSubmissionBlockActions } from "@store/actions/mips-submission-block/mips-submission-block.actions";
import {
  selectOrganizationSubmissionStatuses,
  selectMipsSubmissionBlockLoading,
} from "@store/selectors/mips-submission-block/mips-submission-block.selectors";
import { SUBMISSION_BLOCK_STEP_NUMBER } from "@services/mips-validation.service";

interface IncompleteStep {
  stepNumber: number;
  stepName: string;
  reason: string;
  field?: string;
}

interface ConfirmationCategory {
  name: string;
  key: "quality" | "pi" | "ia";
  estimatedScore: number;
  maxScore: number;
  estimatedWeight: number;
  maxWeight: number;
}

interface CategoryScoreInputs {
  packet: MipsPacket;
  traditionalPacket: MipsPacket | null;
  mvpPacket: MipsPacket | null;
  quality: any;
  pi: any;
  ia: any;
  multiPacketMode: any;
  questionnaire: MipsQuestionnaire | null;
  thirdPartyKeys: Set<string>;
}

@Component({
  selector: "app-mips-submission-confirmation",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatButtonModule, MatTooltipModule, MatProgressSpinnerModule],
  templateUrl: "./mips-submission-confirmation.component.html",
  styleUrls: ["./mips-submission-confirmation.component.css"],
})
export class MipsSubmissionConfirmationComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();
  private readonly validationService = inject(MipsValidationService);
  private readonly dashboardService = inject(DashboardService);
  private readonly categoryScoreInputs$ = new Subject<CategoryScoreInputs>();

  currentPacket$: Observable<MipsPacket | null>;
  traditionalPacket$: Observable<MipsPacket | null>;
  mvpPacket$: Observable<MipsPacket | null>;
  tinDetails$: Observable<TinEligibilityDetails | undefined>;
  isSubmitting$: Observable<boolean>;
  error$: Observable<string | null>;
  quality$: Observable<any>;
  promotingInteroperability$: Observable<any>;
  improvementActivities$: Observable<any>;
  multiPacketMode$: Observable<any>;
  settings$: Observable<any>;
  drcfStatus$: Observable<any>;
  questionnaire$: Observable<MipsQuestionnaire | null>;
  dashboardMeasures$: Observable<any[]>;
  pageLoading$: Observable<boolean>;
  reviewState$: Observable<{
    submissionId: number | undefined;
    status: ReviewBoardStatus | null;
    hasAdminRights: boolean;
    isSuperUser: boolean;
    isSubmitted: boolean;
  }>;

  confirmationForm: FormGroup;

  selectedCategories: ConfirmationCategory[] = [];
  traditionalCategories: ConfirmationCategory[] = [];
  mvpCategories: ConfirmationCategory[] = [];
  registeredEmail: string = "";
  tin: string = "";
  incompleteSteps: IncompleteStep[] = [];
  readonly submissionBlockStepNumber = SUBMISSION_BLOCK_STEP_NUMBER;
  @Input() isReadOnly = false;
  @Input() isOpenedFromAdminReview = false;
  canSubmit: boolean = false;
  isDataLoaded: boolean = false;
  /** True when the last category-weight recalculation failed — categories keep showing their last-known-good values. Not yet surfaced in the template. */
  categoryScoresUnavailable: boolean = false;
  validationResults: StepValidationResult[] = [];

  currentTab: "traditional" | "mvp" = "traditional";
  isMultiPacketMode = false;
  thirdPartyCategories: string[] = [];
  isUpdating$: Observable<boolean>;

  constructor() {
    this.currentPacket$ = this.store.select(selectActivePacket);
    this.traditionalPacket$ = this.store.select(selectTraditionalPacket);
    this.mvpPacket$ = this.store.select(selectMvpPacket);
    this.tinDetails$ = this.store.select(selectTinDetails);
    this.isSubmitting$ = this.store.select(selectIsSubmitting);
    this.error$ = this.store.select(selectError);
    this.quality$ = this.store.select(selectQuality);
    this.promotingInteroperability$ = this.store.select(
      selectPromotingInteroperability,
    );
    this.improvementActivities$ = this.store.select(
      selectImprovementActivities,
    );
    this.multiPacketMode$ = this.store.select(selectMultiPacketMode);
    this.settings$ = this.store.select(selectSettings);
    this.drcfStatus$ = this.store.select(selectDrcfStatus);
    this.questionnaire$ = this.store.select(selectEffectiveQuestionnaire);
    this.dashboardMeasures$ = this.store.select(selectMeasures);

    this.pageLoading$ = combineLatest([
      this.store.select(selectMipsSubmissionBlockLoading),
      this.store.select(selectReviewBoardStatusLoading),
      this.store.select(selectDrcfEnvelopesLoading),
    ]).pipe(
      map(([blockStatusLoading, reviewStatusLoading, drcfLoading]) =>
        blockStatusLoading || reviewStatusLoading || drcfLoading,
      ),
    );

    this.reviewState$ = combineLatest([
      this.currentPacket$,
      this.store.select(selectReviewBoardStatus),
      this.store.select(selectHasAdminRights),
      this.store.select(selectIsSuperUser),
    ]).pipe(
      map(([packet, statusResponse, hasAdminRights, isSuperUser]) => ({
        submissionId: packet?.id,
        status: statusResponse?.status ?? null,
        hasAdminRights,
        isSuperUser,
        isSubmitted: packet?.status === 'submitted',
      })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const packetSaving$ = this.store.select(selectIsSaving);
    const questionnaireSaving$ = this.store.select(selectQuestionnaireIsSubmitting);
    this.isUpdating$ = combineLatest([packetSaving$, questionnaireSaving$]).pipe(
      map(([p, q]) => p || q),
      distinctUntilChanged(),
      switchMap(saving => saving
        ? merge(of(true), timer(5000).pipe(map(() => false)))
        : of(false)
      ),
      distinctUntilChanged(),
    );

    this.confirmationForm = this.fb.group({
      tinVerification: [false, Validators.requiredTrue],
      drcfAttestation: [false, Validators.requiredTrue],
      dataAccuracy: [false, Validators.requiredTrue],
    });
  }

  ngOnInit(): void {
    this.store.dispatch(MipsSubmissionBlockActions.loadOrganizationStatuses());

    this.currentPacket$
      .pipe(
        map((packet) => packet?.id),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((submissionId) => {
        if (submissionId) {
          this.store.dispatch(ReviewBoardActions.loadStatus({ submissionId }));

          if (this.isOpenedFromAdminReview) {
            this.confirmationForm.patchValue({
              tinVerification: true,
              drcfAttestation: true,
              dataAccuracy: true,
            });
          }
        }
      });

    this.categoryScoreInputs$
      .pipe(
        map((inputs) => ({ inputs, request: this.buildReweightingRequest(inputs) })),
        // Most combineLatest emissions below (drcf polling, block-status loading, org status
        // refresh, etc.) don't change anything the reweighting request actually depends on —
        // compare the built request itself so those don't trigger a redundant POST.
        distinctUntilChanged((prev, curr) => JSON.stringify(prev.request) === JSON.stringify(curr.request)),
        debounceTime(300),
        switchMap(({ inputs, request }) =>
          this.dashboardService.calculateMipsReweighting(request).pipe(
            map((response) => ({ inputs, response })),
            catchError((error) => {
              console.error("Failed to calculate MIPS reweighting for submission confirmation", error);
              this.categoryScoresUnavailable = true;
              return of(null);
            }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if (!result) return;
        this.categoryScoresUnavailable = false;
        this.applyCategoriesFromScores(result.inputs, result.response);
      });

    combineLatest([
      this.currentPacket$,
      this.traditionalPacket$,
      this.mvpPacket$,
      this.tinDetails$,
      this.settings$,
      this.quality$,
      this.promotingInteroperability$,
      this.improvementActivities$,
      this.drcfStatus$,
      this.multiPacketMode$,
      this.questionnaire$,
      this.dashboardMeasures$,
      this.store.select(selectOrganizationSubmissionStatuses),
      this.store.select(selectMipsSubmissionBlockLoading),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([
          packet,
          traditionalPacket,
          mvpPacket,
          tinDetails,
          settings,
          quality,
          pi,
          ia,
          drcf,
          multiPacketMode,
          questionnaire,
          dashboardMeasures,
          orgSubmissionStatuses,
          isBlockStatusLoading,
        ]) => {
          if (packet) {
            this.isDataLoaded = true;
            this.isMultiPacketMode = multiPacketMode?.enabled ?? false;
            this.thirdPartyCategories =
              this.computeThirdPartyCategories(questionnaire);

            this.loadSubmissionData(
              packet,
              traditionalPacket,
              mvpPacket,
              tinDetails,
              quality,
              pi,
              ia,
              multiPacketMode,
              questionnaire,
            );

            let validationQuality = quality;
            let validationDrcf = drcf;
            let validationPI = pi;

            if (multiPacketMode?.enabled) {
              const tradQuality = traditionalPacket?.quality;
              const mvpQuality = mvpPacket?.quality;
              if (quality) {
                validationQuality = {
                  ...quality,
                  traditionalMeasures: tradQuality?.traditionalMeasures || quality.traditionalMeasures || [],
                  mvpMeasures: mvpQuality?.mvpMeasures || quality.mvpMeasures || [],
                  mvpMeasuresSelected: mvpQuality?.mvpMeasuresSelected ?? quality?.mvpMeasuresSelected ?? 0,
                  traditionalMeasuresSelected: tradQuality?.traditionalMeasuresSelected ?? quality?.traditionalMeasuresSelected ?? 0,
                  selectedMeasureIds: [
                    ...(tradQuality?.selectedMeasureIds || []),
                    ...(mvpQuality?.selectedMeasureIds || []),
                  ],
                };
              }

              const tradDrcf = traditionalPacket?.drcfStatus;
              const mvpDrcf = mvpPacket?.drcfStatus;
              const tradDocs = tradDrcf?.documents || [];
              const mvpDocs = mvpDrcf?.documents || [];
              validationDrcf = {
                documents: [...tradDocs, ...mvpDocs],
                allDocumentsSigned:
                  tradDocs.length > 0 &&
                  mvpDocs.length > 0 &&
                  (tradDrcf?.allDocumentsSigned || false) &&
                  (mvpDrcf?.allDocumentsSigned || false),
              };

              const tradPI = traditionalPacket?.promotingInteroperability;
              const mvpPI = mvpPacket?.promotingInteroperability;
              const tradIncomplete = tradPI?.incompleteMeasureIds || [];
              const mvpIncomplete = mvpPI?.incompleteMeasureIds || [];
              const allIncomplete = [...new Set([...tradIncomplete, ...mvpIncomplete])];
              validationPI = {
                ...pi,
                incompleteMeasureIds: allIncomplete.length > 0 ? allIncomplete : (pi?.incompleteMeasureIds || []),
              };
            }

            const orgStatus = orgSubmissionStatuses.find(
              (status) => status.organizationId === Number(packet.orgId),
            );

            this.validationResults = this.validationService.validateAllSteps({
              packet,
              tinDetails,
              settings,
              quality: validationQuality,
              pi: validationPI,
              ia,
              drcf: validationDrcf,
              multiPacketMode,
              traditionalIA: multiPacketMode?.enabled ? traditionalPacket?.improvementActivities : undefined,
              mvpIA: multiPacketMode?.enabled ? mvpPacket?.improvementActivities : undefined,
              questionnaire,
              dashboardMeasures,
              submissionBlock: {
                isBlocked: orgStatus?.isBlocked ?? false,
                reasonCodes: orgStatus?.reasonCodes ?? [],
              },
            });

            this.canSubmit = this.validationResults.every((r) => r.canSubmit) && !isBlockStatusLoading;

            this.incompleteSteps = this.validationResults
              .filter((r) => !r.canSubmit)
              .flatMap((r) =>
                r.issues
                  .filter((issue) => issue.severity === "error")
                  .map((issue) => ({
                    stepNumber: r.stepNumber,
                    stepName: r.stepName,
                    reason: issue.message,
                    field: issue.field,
                  })),
              );
          } else {
            this.isDataLoaded = false;
          }
        },
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSubmissionData(
    packet: MipsPacket,
    traditionalPacket: MipsPacket | null,
    mvpPacket: MipsPacket | null,
    tinDetails: TinEligibilityDetails | undefined,
    quality: any,
    pi: any,
    ia: any,
    multiPacketMode: any,
    questionnaire: MipsQuestionnaire | null,
  ): void {
    if (tinDetails) {
      this.registeredEmail = tinDetails.emailForFeedback;
      this.tin = tinDetails.tin;
    }

    const thirdPartyKeys = this.getExcludedCategoryKeys(questionnaire);

    this.categoryScoreInputs$.next({
      packet,
      traditionalPacket,
      mvpPacket,
      quality,
      pi,
      ia,
      multiPacketMode,
      questionnaire,
      thirdPartyKeys,
    });
  }

  /**
   * Builds the same request shape the dashboard gauge sends to dashboard/reweighting, so the
   * stepper's category weights come from the identical backend computation instead of a second,
   * independently-maintained rounding implementation.
   */
  private buildReweightingRequest(inputs: CategoryScoreInputs): CalculateMipsReweightingRequest {
    const { packet, quality, pi, ia, traditionalPacket, mvpPacket, multiPacketMode, questionnaire } = inputs;
    const isMulti = multiPacketMode?.enabled ?? false;
    const packetType: "traditional" | "mvp" = packet?.reportingType === "mvp" ? "mvp" : "traditional";

    const tradQuality = isMulti ? traditionalPacket?.quality || quality : packetType === "traditional" ? quality : null;
    const tradPI = isMulti ? traditionalPacket?.promotingInteroperability || pi : packetType === "traditional" ? pi : null;
    const tradIA = isMulti ? traditionalPacket?.improvementActivities || ia : packetType === "traditional" ? ia : null;

    const mvpQuality = isMulti ? mvpPacket?.quality || quality : packetType === "mvp" ? quality : null;
    const mvpPI = isMulti ? mvpPacket?.promotingInteroperability || pi : packetType === "mvp" ? pi : null;
    const mvpIA = isMulti ? mvpPacket?.improvementActivities || ia : packetType === "mvp" ? ia : null;

    const hardshipQuality = questionnaire?.appliedHardship === "yes" && questionnaire.hardshipQuality === "yes";
    const hardshipPi = questionnaire?.appliedHardship === "yes" && questionnaire.hardshipPromotingInteroperability === "yes";
    const hardshipIa = questionnaire?.appliedHardship === "yes" && questionnaire.hardShipImprovementActivities === "yes";
    const isSmallPractice = questionnaire?.specialStatusSmall === "yes";

    return {
      traditional: buildQualityAttestationInput(
        tradQuality?.estimatedPoints ?? 0,
        tradQuality?.estimatedTotal ?? 0,
        60,
        tradIA?.estimatedPoints ?? null,
        tradPI?.estimatedTotal ?? null,
      ),
      mvp: buildQualityAttestationInput(
        mvpQuality?.estimatedPoints ?? 0,
        mvpQuality?.estimatedTotal ?? 0,
        40,
        mvpIA?.estimatedPoints ?? null,
        mvpPI?.estimatedTotal ?? null,
      ),
      hardshipQuality,
      hardshipPi,
      hardshipIa,
      hardshipCost: false,
      isSmallPractice,
    };
  }

  private applyCategoriesFromScores(inputs: CategoryScoreInputs, response: CalculateMipsReweightingResponse): void {
    const { packet, quality, pi, ia, traditionalPacket, mvpPacket, multiPacketMode, thirdPartyKeys } = inputs;

    if (multiPacketMode?.enabled) {
      const tradQuality = traditionalPacket?.quality || quality;
      const tradPI = traditionalPacket?.promotingInteroperability || pi;
      const tradIA = traditionalPacket?.improvementActivities || ia;

      this.traditionalCategories = this.buildCategoriesForPacket(
        "traditional",
        tradQuality,
        tradPI,
        tradIA,
        response.weights,
        response.traditional,
        thirdPartyKeys,
      );

      const mvpQuality = mvpPacket?.quality || quality;
      const mvpPI = mvpPacket?.promotingInteroperability || pi;
      const mvpIA = mvpPacket?.improvementActivities || ia;

      this.mvpCategories = this.buildCategoriesForPacket(
        "mvp",
        mvpQuality,
        mvpPI,
        mvpIA,
        response.weights,
        response.mvp,
        thirdPartyKeys,
      );

      this.selectedCategories =
        this.currentTab === "traditional"
          ? this.traditionalCategories
          : this.mvpCategories;
    } else {
      const packetType =
        packet?.reportingType === "mvp" ? "mvp" : "traditional";
      const scoreBlock = packetType === "mvp" ? response.mvp : response.traditional;
      this.selectedCategories = this.buildCategoriesForPacket(
        packetType,
        quality,
        pi,
        ia,
        response.weights,
        scoreBlock,
        thirdPartyKeys,
      );
      this.traditionalCategories = [];
      this.mvpCategories = [];
    }
  }

  /** Renders weighted scores exactly as returned by dashboard/reweighting — no local rounding. */
  private buildCategoriesForPacket(
    packetType: "traditional" | "mvp",
    quality: any,
    pi: any,
    ia: any,
    weights: MipsCategoryWeights,
    scoreBlock: DashboardMipsScoreBlock,
    thirdPartyKeys: Set<string>,
  ): ConfirmationCategory[] {
    const categories: ConfirmationCategory[] = [];
    const maxQualityPoints = packetType === "mvp" ? 40 : 60;

    if (quality && !thirdPartyKeys.has("quality")) {
      const qualityScore =
        Math.round((quality.estimatedTotal ?? 0) * 10) / 10;
      categories.push({
        name: "Quality",
        key: "quality",
        estimatedScore: qualityScore,
        maxScore: maxQualityPoints,
        estimatedWeight: scoreBlock?.qualityWeightedScore ?? 0,
        maxWeight: weights.quality,
      });
    }

    if (pi && !thirdPartyKeys.has("pi")) {
      const piScore = Math.round(pi.estimatedTotal ?? 0);
      categories.push({
        name: "Promoting Interoperability",
        key: "pi",
        estimatedScore: piScore,
        maxScore: 100,
        estimatedWeight: scoreBlock?.piWeightedScore ?? 0,
        maxWeight: weights.pi,
      });
    }

    if (ia && !thirdPartyKeys.has("ia")) {
      const iaScore = Math.round(ia.estimatedPoints ?? 0);
      categories.push({
        name: "Improvement Activities",
        key: "ia",
        estimatedScore: iaScore,
        maxScore: 40,
        estimatedWeight: scoreBlock?.iaWeightedScore ?? 0,
        maxWeight: weights.ia,
      });
    }

    return categories;
  }

  private getExcludedCategoryKeys(
    questionnaire: MipsQuestionnaire | null,
  ): Set<string> {
    return this.validationService.getThirdPartyCategories(questionnaire);
  }

  private computeThirdPartyCategories(
    questionnaire: MipsQuestionnaire | null,
  ): string[] {
    const excluded = this.validationService.getThirdPartyCategories(questionnaire);
    const keyToName: Record<string, string> = {
      quality: "Quality",
      pi: "Promoting Interoperability",
      ia: "Improvement Activities",
    };
    return Array.from(excluded)
      .map((key) => keyToName[key])
      .filter((name): name is string => !!name);
  }

  onOpenSubmissionCheck(submissionId: number): void {
    this.dialog.open(MipsSubmissionCheckDialogComponent, {
      width: "1100px",
      maxWidth: "95vw",
      data: { submissionId },
    });
  }

  onReadyToSubmit(submissionId: number): void {
    this.store.dispatch(ReviewBoardActions.requestReview({ submissionId }));
  }

  onCancelReview(submissionId: number): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: "Cancel Review",
        content: "Are you sure you want to cancel this review? Reg-Ent will no longer submit this to CMS on your behalf.",
        confirmText: "Cancel Review",
        cancelText: "Close",
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.store.dispatch(ReviewBoardActions.cancelReview({ submissionId }));
      }
    });
  }

  onTabChange(tab: "traditional" | "mvp"): void {
    this.currentTab = tab;
    this.selectedCategories =
      tab === "traditional"
        ? this.traditionalCategories
        : this.mvpCategories;
  }

  onSubmit(): void {
    if (this.confirmationForm.invalid) {
      this.confirmationForm.markAllAsTouched();
      return;
    }

    if (!this.canSubmit) {
      return;
    }
    combineLatest([this.currentPacket$, this.multiPacketMode$])
      .pipe(take(1))
      .subscribe(([packet, multiPacketMode]) => {
        if (!packet?.id) {
          console.error("No packet ID available");
          return;
        }

        if (
          multiPacketMode?.enabled &&
          multiPacketMode.traditionalPacketId &&
          multiPacketMode.mvpPacketId
        ) {
          this.store.dispatch(
            MipsPacketActions.submitMultiplePackets({
              packetIds: [
                multiPacketMode.traditionalPacketId,
                multiPacketMode.mvpPacketId,
              ],
            }),
          );
        } else {
          this.store.dispatch(
            MipsPacketActions.submitPacket({ packetId: packet.id }),
          );
        }
      });
  }

  onCancel(): void {
    this.router.navigate(["/mipsattest"]);
  }

  goToStep(stepNumber: number): void {
    this.store.dispatch(
      MipsPacketActions.navigateToStep({ stepIndex: stepNumber - 1 }),
    );
  }

  refreshMvpRegistration(): void {
    this.store.dispatch(MipsPacketActions.refreshMVPRegistration());
  }

  get hasQuality(): boolean {
    return this.selectedCategories.some((c) => c.name === "Quality");
  }

  get hasPI(): boolean {
    return this.selectedCategories.some(
      (c) => c.name === "Promoting Interoperability",
    );
  }

  get hasIA(): boolean {
    return this.selectedCategories.some(
      (c) => c.name === "Improvement Activities",
    );
  }

  get hasThirdPartyExclusions(): boolean {
    return this.thirdPartyCategories.length > 0;
  }

  get tinVerification() {
    return this.confirmationForm.get("tinVerification");
  }

  get drcfAttestation() {
    return this.confirmationForm.get("drcfAttestation");
  }

  get dataAccuracy() {
    return this.confirmationForm.get("dataAccuracy");
  }
}
